/**
 * Generates a cryptographically secure random password string
 * @param {Object} options Configuration options
 * @param {number} [options.length=16] Length of the password (default: 16)
 * @param {boolean} [options.numbers=true] Include numbers in the password
 * @param {boolean} [options.symbols=true] Include special symbols in the password
 * @returns {string} The generated password
 * @throws {Error} If length is less than 1 or if no character sets are selected
 */
export default function generatePassword({
  length = 16,
  numbers = true,
  symbols = true,
}: { length?: number; numbers?: boolean; symbols?: boolean } = {}): string {
  if (length < 1) {
    throw new Error('Password length must be at least 1');
  }

  const lowercase = 'abcdefghijklmnopqrstuvwxyz';
  const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const nums = '0123456789';
  const specialChars = '!@#$%^&*()_+-=[]{}|;:,.<>?';

  // Always include lowercase and uppercase letters
  let chars = lowercase + uppercase;

  // Add optional character sets
  if (numbers) chars += nums;
  if (symbols) chars += specialChars;

  if (chars.length === 0) {
    throw new Error('At least one character set must be selected');
  }

  // Generate cryptographically secure random values
  const array = new Uint32Array(length);
  crypto.getRandomValues(array);

  // Convert random values to characters
  let password = '';
  for (let i = 0; i < length; i += 1) {
    password += chars[array[i] % chars.length];
  }

  return password;
}
